import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, GoogleMapProps } from '@react-google-maps/api'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import cn from 'clsx'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useSearchMapResults from './useSearchMapResults'
import { EnsureGoogleMapsLoaded, useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import usePrevious from 'hooks/usePrevious'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import * as InteractionStudioService from 'api/interactionStudio'
import {
  areOffersBedbank,
  centerAndZoomOnMarker,
  centrePinForMobile,
  fitMarkersOnMap,
  getCenterOfCluster,
  getMarkersByOffers,
  getOfferId,
  getPadding,
  isLoadedOffer,
  isMarkerWithinBounds,
  mapHasAnyMarkersInView,
  parseBounds,
  subtractPaddingFromBounds,
  useVisitedMarkersState,
} from './utils'

import config from 'constants/config'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { useAppSelector } from 'hooks/reduxHooks'
import { ANYWHERE_PLACE_ID, BALI_LAT_LNG } from 'constants/search'
import HotelMapMarker from 'components/Pages/HotelSearchPage/HotelMapMarker/HotelMapMarker'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import TextButton from 'components/Luxkit/Button/TextButton'
import { clusterMarkersByProximity } from './clusterUtils'
import ClusterMarker from './ClusterMarker'
import SearchTimeLogger from 'components/Search/SearchResultsMap/SearchTimeLogger'
import { endTracking, startTracking } from 'services/searchTimeLoggingService'
import { sortBy, take } from 'lib/array/arrayUtils'
import Snackbar from 'components/Luxkit/Snackbar/Snackbar'
import useOffers from 'hooks/Offers/useOffers'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import TripItemCardSingle from './TripPlanner/TripItemCardSingle'
import SearchMapMarker from './TripPlanner/SearchMapMarker'
import TripPlannerToggle from './TripPlanner/TripPlannerToggle'
import useTripPlannerPins from './useTripPlannerPins'
import uuidV4 from 'lib/string/uuidV4Utils'
import BedbankMapMarker from 'components/Pages/HotelSearchPage/HotelMapMarker/BusinessTravellerBedbankMapMarker'

const MapContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const FloatingBanner = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const FadableTripToggle = styled(TripPlannerToggle)`
  transition: opacity 0.2s;

  &.isInBackground {
    opacity: 0.7;
  }
`

export enum HoveredPinType {
  FLASH = 'flash',
  LE = 'le',
  BEDBANK = 'bedbank',
  NONE = 'none',
}

export const TRIP_PLANNER_ALL_TRIPS_OPTION = 'All trips'
export const TRIP_PLANNER_HIDE_ALL_TRIPS_OPTION = 'Hide all trips'

export interface HoveredOffer {
  id: string;
  type: string;
}

interface Props {
  filters: App.OfferListFilters,
  onBoundsChanged: (newBounds: google.maps.LatLngBounds) => void,
  onSelectOffers: (offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>) => void,
  onDeselectOffers: () => void,
  onError: () => void,
  onZeroResults: () => void,
  selectedOfferIds: Array<string>,
  handleProductClick?: (offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary, position: number) => void,
  onHover?: (offerId: string, type: string) => void,
  rootRef?: React.RefObject<HTMLDivElement>,
  offerListOverride?: App.OfferList,
  /**
   * @deprecated This attribute is not to be used
   */
  fallbackOfferList?: App.OfferList,
  pageSize?: number,
  page?: number,
  showClusterMarkers: boolean,
  offerList?: App.OfferList,
  searchStartTimeMillis: number,
  fullScreenMap: boolean,
  zeroResultsBanner: boolean,
}

const MAP_OPTIONS: GoogleMapProps['options'] = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControlOptions: {
    position: 9,
  },
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      west: -180,
      east: 180,
    },
    strictBounds: true,
  },
  minZoom: 3,
  maxZoom: 19,
  mapId: config.GOOGLE_MAP_ID,
  clickableIcons: false,
}

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const typeOrder: Record<string, number> = {
  hotel: 1,
  tactical_ao_hotel: 2,
  bedbank_hotel: 3,
}

const MAX_ZOOM_FOR_CLUSTERING = 12

const MAX_ZOOM_FOR_LARGE_CLUSTERS = 10

const MIN_MARKERS_TO_DISPLAY = 20
const MARKER_DISPLAY_TIMEOUT = 2400

// switch to true if you want to show the page markers as single markers, and have the rest
// of the offers shown as clusters
const shouldAlwaysShowPageOffers = false

function isTrackableProduct(offer: App.Offer | App.BedbankOffer | App.OfferListMetaData): offer is App.Offer | App.BedbankOffer {
  return 'id' in offer
}

interface DisplayMarkersParams {
  currentOfferCount: number;
  markersToDisplay: number;
  fetchingList: boolean;
  isNewManualSearch: boolean;
  isNewlyLoaded: boolean;
  searchStartTimeMillis: number;
}

function displayMarkers({
  currentOfferCount,
  markersToDisplay,
  fetchingList,
  isNewManualSearch,
  isNewlyLoaded,
  searchStartTimeMillis,
}: DisplayMarkersParams): boolean {
  const timeElapsed = new Date().getTime() - searchStartTimeMillis
  const displayThresholdMet = (timeElapsed < MARKER_DISPLAY_TIMEOUT && currentOfferCount >= MIN_MARKERS_TO_DISPLAY) ||
    timeElapsed >= MARKER_DISPLAY_TIMEOUT || (timeElapsed >= MARKER_DISPLAY_TIMEOUT && currentOfferCount >= MIN_MARKERS_TO_DISPLAY)

  return displayThresholdMet || (currentOfferCount >= markersToDisplay) && !fetchingList || (displayThresholdMet && isNewManualSearch && isNewlyLoaded)
}

function SearchResultsMap(props: Props) {
  const {
    filters,
    onBoundsChanged,
    onSelectOffers,
    onDeselectOffers,
    onError,
    onZeroResults,
    selectedOfferIds,
    handleProductClick,
    rootRef,
    offerListOverride,
    pageSize,
    page,
    onHover,
    showClusterMarkers = false,
    offerList,
    searchStartTimeMillis,
    fullScreenMap,
    zeroResultsBanner,
  } = props

  const {
    searchTargetLabel,
  } = useContext(GlobalSearchStateContext)

  const {
    isLoaded: isGoogleMapsLoaded,
  } = useGoogleMapsContext()

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const [hoveredOffer, setHoveredOffer] = useState<HoveredOffer | undefined>(undefined)
  const [recentSearchResults, setRecentSearchResults] = useState<Array<Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>>>([])
  const [markers, setMarkers] = useState<Array<App.SearchMapMarker>>([])
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const setSearchLabel = useCallback((searchLabel: string) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL, searchTargetLabel: searchLabel })
  }, [globalSearchDispatch])

  const isAnyTimeSearch = (!filters?.checkIn && !filters?.checkOut && !filters?.flexibleNights && !filters?.flexibleMonths)

  const zoom = map?.getZoom()
  const currentSearchResult = useSearchMapResults(
    filters,
    selectedOfferIds,
    offerListOverride,
    (showClusterMarkers && isAnyTimeSearch),
    pageSize,
    offerList,
  )

  const { allOffersLoaded, fetchingPage } = currentSearchResult
  const currentOfferListKey = currentSearchResult.key || uuidV4()

  useEffect(() => {
    if (offerListOverride) {
      // add pagination time tracking
      if (startTracking('map-loading-time', filters)) {
        endTracking('map-loading-time')
      }
    }
  }, [filters, offerListOverride])

  const searchSessionId = useAppSelector(state => state.analytics.search.sessions[currentOfferListKey])

  const getTrackingDataFromMarker = useCallback((marker: App.SearchMapMarker, pinType: string): InteractionStudioService.TrackMapPinClickParams => {
    const offer = marker.offers[0]
    return {
      offerId: getOfferId(offer),
      offerType: offer.type,
      pinType,
      price: undefined,
      listId: searchSessionId,
      index: 0,
    }
  }, [searchSessionId])
  const [isProjectionLoaded, setIsProjectionLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  /*
    Normally when the map goes idle, it means the user has manually panned/zoomed/rotated,
    and we should do a new bounds search.
    But it also happens when the map loads, or when the map bounds are moved programmatically,
    or when the map size changes due to resizing the window or dismissing a notification.
    We can distinguish between these scenarios with a combination of:
    - Tracking whether the map has been panned/zoomed/rotated, via `hasInteracted` ref
    - Explicitly ignoring the next `onIdle` event, via `ignoreNextIdle` ref
    Neither method on its own is quite enough to handle all edge cases.
    Ideally the map would have a built-in callback for this, but it doesn't.
  */
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const onInteract = useCallback(() => {
    hasInteracted.current = true
  }, [])

  // Check if this is a new destination or property search, in which case we need to update the map position
  const prevAllOffersLoaded = usePrevious(allOffersLoaded, false)
  const prevFilters = usePrevious(filters, {})
  const prevOfferListKey = usePrevious(currentOfferListKey)
  const isNewlyLoaded = allOffersLoaded && (currentOfferListKey !== prevOfferListKey || !prevAllOffersLoaded) && !fetchingPage
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters.destinationId
  const isNewProperty = !!filters.propertyId && filters.propertyId !== prevFilters.propertyId
  const isNewLandmark = !!filters.landmarkId && filters.landmarkId !== prevFilters.landmarkId
  const hasDates = !!(filters.checkIn && filters.checkOut)
  const isNewDates = hasDates && (filters.checkIn !== prevFilters.checkIn || filters.checkOut !== prevFilters.checkOut)
  const isNewRooms = JSON.stringify(filters.rooms) !== JSON.stringify(prevFilters.rooms)
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters.bounds
  const isNewHolidayTypes = JSON.stringify(filters.holidayTypes) !== JSON.stringify(prevFilters.holidayTypes)
  const isNewAmenities = JSON.stringify(filters.amenities) !== JSON.stringify(prevFilters.amenities)
  // we distinguish between when bounds are changed vs when filters are changed, because for the former we can still show previous results
  const isNewManualSearch = isNewDestination || isNewProperty || isNewDates || isNewRooms || isNewHolidayTypes || isNewAmenities
  const fetchingList = (offerList?.offerIds?.length || 0) < (offerListOverride?.offerCount || 0)

  const isNewSearch = isNewManualSearch || isNewBounds
  const lastSearch = useRef({
    destination: false,
    property: false,
    landmark: false,
    bounds: false,
    dates: false,
    rooms: false,
  })
  if (isNewSearch) {
    lastSearch.current = {
      destination: isNewDestination,
      property: isNewProperty,
      landmark: isNewLandmark,
      bounds: isNewBounds,
      dates: isNewDates,
      rooms: isNewRooms,
    }
  }

  // When the filters have changed, we should continue showing the offers from the last few offer lists
  useEffect(() => {
    setRecentSearchResults([currentSearchResult.offers])
  }, [currentSearchResult.offers])

  useEffect(() => {
    if (currentSearchResult.error) {
      onError()
    }
  }, [currentSearchResult.error, onError])

  const currentOffers = useMemo(() => {
    const [_kind, id] = filters.propertyId?.split(':') || []
    return sortBy(recentSearchResults.flat().filter(offer => !('isSoldOut' in offer) || !offer.isSoldOut || (id && offer.property?.id === id)),
      value => typeOrder[value.type ?? ''],
      'asc',
    )
  }, [filters.propertyId, recentSearchResults])

  const loadedOffers = useMemo(() => currentOffers.filter(isLoadedOffer), [currentOffers])
  const currentOffersBestPrices = useOffersBestPrices(loadedOffers, filters)
  const currentBounds = useMemo(() => isGoogleMapsLoaded && filters.bounds !== undefined ? parseBounds(filters.bounds) : undefined, [filters.bounds, isGoogleMapsLoaded])

  const currentMarkers = useMemo(() => {
    const offerMarkers = getMarkersByOffers(currentOffers, true)
    const displayOfferMarkers = offerMarkers.filter(
      (marker) => !marker.offers.every(
        (offer) => currentOffersBestPrices[getOfferId(offer)]?.soldOut,
      ),
    ) // filter out, sold out offers
    const markerResult = currentBounds ? displayOfferMarkers.filter((marker) => isMarkerWithinBounds(marker, currentBounds)) : displayOfferMarkers
    const limit = zoom && zoom > MAX_ZOOM_FOR_CLUSTERING ? config.search.mapView.pinLimit + config.search.mapView.additionalPins : config.search.mapView.clusterPinLimit
    if (limit) {
      return take(markerResult, limit)
    }
    return markerResult
  }, [currentBounds, currentOffers, currentOffersBestPrices, zoom])

  useEffect(() => {
    if (displayMarkers({ currentOfferCount: currentOffers.length, markersToDisplay: offerListOverride?.offerIds.length || 0, fetchingList, isNewManualSearch, isNewlyLoaded, searchStartTimeMillis })) {
      setMarkers(currentMarkers)
    } else if (currentSearchResult.noOffersAvailable && !fetchingPage) {
      setMarkers([])
    }
  }, [currentMarkers, currentOffers.length, currentSearchResult.noOffersAvailable, fetchingList, fetchingPage, isNewManualSearch, isNewlyLoaded, offerListOverride, searchStartTimeMillis])

  const activeMarkerId = selectedOfferIds.join(',')
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()

  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const [centrePin, setCentrePin] = useState(true)
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const findSelectedMarker = useCallback((markers: Array<App.SearchMapMarker>, selectedId: string) =>
    markers.find(marker => marker.offers.some(offer => getOfferId(offer) === selectedId))
  , [])

  const handleMobilePinCentering = useCallback((clickedMarker?: App.SearchMapMarker) => {
    // Early returns for invalid states
    if (isTabletScreenSizeAndUp || !map) return
    if (!clickedMarker && !centrePin) return

    // Use clicked marker or find selected marker
    const markerToCenter = clickedMarker || findSelectedMarker(markers, selectedOfferIds[0])

    if (markerToCenter) {
      centrePinForMobile(map, markerToCenter)
    }
  }, [isTabletScreenSizeAndUp, map, centrePin, findSelectedMarker, markers, selectedOfferIds])

  const onMarkerClick = useCallback((marker: App.SearchMapMarker, pinType: string, e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setCentrePin(true)
    handleMobilePinCentering(marker)
    onSelectOffers(marker.offers)

    const offer = marker.offers[0]
    if (isTrackableProduct(offer)) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position: 0,
      })
    }

    InteractionStudioService.trackMapPinClick(getTrackingDataFromMarker(marker, pinType))
    if (e) {
      e.stopPropagation()
    }
  }, [handleMobilePinCentering, onSelectOffers, getTrackingDataFromMarker, dispatchOfferListEvent])

  const onClusterClick = useCallback((markers: Array<App.SearchMapMarker>) => {
    fitMarkersOnMap(map, markers)
  }, [map])

  // Determine whether to report zero results
  const hadMarkersLoadedOrInViewAtLastLoad = useRef(false)
  useEffect(() => {
    if (isNewlyLoaded) {
      const offersLoadedOrInView = currentSearchResult.totalNumOffers > 0 || mapHasAnyMarkersInView(map, markers)
      if (
        !offersLoadedOrInView &&
        !currentSearchResult.error &&
        hadMarkersLoadedOrInViewAtLastLoad.current
      ) {
        onZeroResults()
      }
      hadMarkersLoadedOrInViewAtLastLoad.current = offersLoadedOrInView
    }
  }, [currentSearchResult, isNewlyLoaded, map, markers, onZeroResults])

  // If user searched for a property, select it. If user searched for a destination, deselect offers
  useEffect(() => {
    if (isNewlyLoaded) {
      if (!filters.destinationId && searchTargetLabel !== 'Map area') {
        setSearchLabel('Map area')
      }
      if (lastSearch.current.property && filters.propertyId) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(
          marker => marker.offers.some(
            offer => 'property' in offer && offer.property?.id === id,
          ),
        )
        if (marker) {
          onMarkerClick(marker, 'pin')
        }
      }
    }
  }, [isNewlyLoaded, markers, selectedOfferIds.length, filters.propertyId, onMarkerClick, onDeselectOffers, filters.destinationId, setSearchLabel, searchTargetLabel])

  const {
    tripPlannerLEItemOfferIds,
    dropDownVisible,
    deselectTripItem,
    LEandCustomTripPlannerMarkers,
    canShowTripPlannerToggle,
    handleTripPlannerItemSelect,
    updateTripPlannerMarkers,
    selectedTrip,
    selectedTripItem,
    handleSelectTrip,
    trips,
    toggleDropdownVisibility,
    tripItemCardSingleRef,
  } = useTripPlannerPins({
    map,
    markers,
    onDeselectOffers,
  })

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => {
    ignoreNextIdle.current = true
    if (filters.destinationId === ANYWHERE_PLACE_ID) {
      map.setCenter(BALI_LAT_LNG)
      map.setZoom(2)
    } else if (filters.propertyId) {
      const [_kind, id] = filters.propertyId.split(':')
      const marker = markers.find(
        marker => marker.offers.some(
          offer => 'property' in offer && offer.property?.id === id,
        ),
      )
      if (marker) {
        centerAndZoomOnMarker(map, marker, true)
      } else {
        fitMarkersOnMap(map, markers)
      }
    } else if (filters.bounds && currentBounds) {
      map.fitBounds(currentBounds, getPadding(true))
    } else {
      if (!selectedOfferIds.length && markers.length) {
        fitMarkersOnMap(map, markers)
      }
    }
    setMap(map)
  }, [currentBounds, filters.bounds, filters.destinationId, filters.propertyId, markers, selectedOfferIds])

  // Update the bounds if the markers have changed
  useEffect(() => {
    if (map && !fetchingPage && (lastSearch.current.destination || lastSearch.current.property || lastSearch.current.landmark)) {
      ignoreNextIdle.current = true

      if (lastSearch.current.property && filters.propertyId) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(
          marker => marker.offers.some(
            offer => 'property' in offer && offer.property?.id === id,
          ),
        )
        if (marker) {
          centerAndZoomOnMarker(map, marker, true)
          handleMobilePinCentering()
          return
        }
      }
      if (filters.destinationId === ANYWHERE_PLACE_ID) {
        map.setCenter(BALI_LAT_LNG)
        map.setZoom(2)
      } else {
        fitMarkersOnMap(map, markers)
        handleMobilePinCentering()
      }
    }
  }, [filters.destinationId, filters.propertyId, filters.landmarkId, fetchingPage, map, markers, selectedOfferIds.length, handleMobilePinCentering])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // Maintain mapping of `google.maps.Marker` instances to the source `App.SearchMapMarker` objects
  // so that the clusterer calculator can figure out what offers are in a cluster
  const markerMap = useRef(new Map<google.maps.Marker, App.SearchMapMarker>())
  const onMarkerLoad = useCallback((markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => {
    markerMap.current.set(markerInstance, markerData)
  }, [])
  const onMarkerUnmount = useCallback((markerInstance: google.maps.Marker) => {
    markerMap.current.delete(markerInstance)
  }, [])

  const onUserMovedMap = useMemo<() => void>(() => debounce(
    () => {
      setCentrePin(false)
      const mapBounds = map?.getBounds()
      const boundingRect = containerRef.current?.getBoundingClientRect()
      if (mapBounds && boundingRect) {
        const boundsWithoutMargin = subtractPaddingFromBounds(mapBounds, boundingRect)
        onBoundsChanged(boundsWithoutMargin)
        updateTripPlannerMarkers(mapBounds)
      }
    },
    2000,
    { leading: true, trailing: true },
  ), [map, onBoundsChanged, updateTripPlannerMarkers])

  const onIdle = useCallback(() => {
    if (!ignoreNextIdle.current && hasInteracted.current) {
      onUserMovedMap()
    }
    ignoreNextIdle.current = false
    hasInteracted.current = false
  }, [onUserMovedMap])

  const isMobile = useScreenSizeOnly('mobile')
  const mapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    minZoom: isMobile ? 2 : MAP_OPTIONS?.minZoom,
    zoomControl: !isMobile,
    disableDoubleClickZoom: !!activeMarkerId,
    zoomControlOptions: {
      position: 3, // Top right zoom controls
    },
  }), [activeMarkerId, isMobile])

  useEffect(() => {
    const root = rootRef?.current
    if (root) {
      const handleOfferHover = (event: CustomEvent) => {
        const id = event.detail.id
        const offerType = event.detail.type
        const persist = event.detail.persist

        if (!id && persist) return

        if (!id && !persist) {
          setHoveredOffer(undefined)
          return
        }

        setHoveredOffer({ id, type: offerType })
      }
      root.addEventListener('offerHover', handleOfferHover)
      return () => {
        root.removeEventListener('offerHover', handleOfferHover)
      }
    }
  }, [rootRef])

  // projection is only loaded after map is fully initialised
  useEffect(() => {
    if (!map) {
      return
    }
    google.maps.event.addListenerOnce(map, 'projection_changed', function() {
      setIsProjectionLoaded(true)
    })
  }, [map])

  const clusterDistance = zoom && zoom > MAX_ZOOM_FOR_CLUSTERING ? 50 : 100

  const { singleMarkers, clusterMarkers } = useMemo(() => {
    if (!markers.length || !map || !isProjectionLoaded) {
      return {}
    }

    const zoom = map.getZoom()
    if (zoom && zoom > MAX_ZOOM_FOR_CLUSTERING) {
      return { singleMarkers: markers, clusterMarkers: [] }
    }
    let pageMarkers: Array<App.SearchMapMarker> = []
    let rest: Array<App.SearchMapMarker> = markers
    if (shouldAlwaysShowPageOffers) {
      const startIndex = page && pageSize ? page * pageSize : 0
      pageMarkers = take(markers, pageSize ?? 20, startIndex)
      rest = markers.slice(startIndex + (pageSize ?? 20))
    }
    const clusters = Object.values(clusterMarkersByProximity(rest, clusterDistance, map))
    const singleMarkers: Array<App.SearchMapMarker> = pageMarkers
    const clusterMarkers: Array<App.SearchMapClusterMarker> = []
    clusters.forEach((cluster) => {
      if (cluster.markers.length > 1) {
        const clusterCenter = getCenterOfCluster(cluster.markers)
        clusterMarkers.push({
          id: cluster.markers[0].id,
          latitude: clusterCenter.latitude,
          longitude: clusterCenter.longitude,
          markers: cluster.markers,
          count: cluster.markers.length,
        })
      } else {
        // don't show single markers if it's a random lone bedbank or LPC offer
        if (zoom && zoom > MAX_ZOOM_FOR_LARGE_CLUSTERS || cluster.markers[0]?.offers?.[0]?.type === OFFER_TYPE_HOTEL) {
          singleMarkers.push(cluster.markers[0])
        }
      }
    })

    return { singleMarkers, clusterMarkers }
  }, [clusterDistance, isProjectionLoaded, map, markers, page, pageSize])

  const singleMarkerOfferIds = singleMarkers?.map((marker) => (
    marker.id
  )) ?? []

  // fetch offer details for only the markers that can be clicked
  useOffers(singleMarkerOfferIds)

  const hotelMapMarkerProps = {
    onMarkerClick,
    onMarkerLoad,
    onMarkerUnmount,
    filters,
    onHover,
    onDeselectOffers,
    handleProductClick,
    mapContainerRef: containerRef,
  }

  function mapClick() {
    onDeselectOffers()
    setHoveredOffer(undefined)
  }

  const nonClusterMarkers = (
    <>
      {markers.map((marker, index) => {
        const isSoldOut = !fetchingPage && marker.offers.every(
          (offer) => currentOffersBestPrices[getOfferId(offer)]?.soldOut,
        )
        const bestPrice = config.businessTraveller.currentAccountMode === 'business' ? currentOffersBestPrices[getOfferId(marker.offers[0])] : undefined
        const isBedbank = areOffersBedbank(marker.offers)
        if (isBedbank) {
          return (
            <BedbankMapMarker
              key={marker.id}
              marker={marker}
              index={index}
              onMarkerClick={onMarkerClick}
              onMarkerLoad={onMarkerLoad}
              onMarkerUnmount={onMarkerUnmount}
              isActive={activeMarkerId === marker.id}
              isHovered={hoveredOffer?.id === marker.id}
              isVisited={visitedMarkerIds.has(marker.id)}
              filters={filters}
              onDeselectOffers={onDeselectOffers}
              handleProductClick={handleProductClick}
              onHover={onHover}
              soldOut={isSoldOut}
              map={map}
            />
          )
        }
        return <HotelMapMarker
          key={marker.id}
          marker={marker}
          index={index}
          onMarkerClick={onMarkerClick}
          onMarkerLoad={onMarkerLoad}
          onMarkerUnmount={onMarkerUnmount}
          isActive={activeMarkerId === marker.id}
          isHovered={hoveredOffer?.id === marker.id}
          isVisited={visitedMarkerIds.has(marker.id)}
          filters={filters}
          onDeselectOffers={onDeselectOffers}
          handleProductClick={handleProductClick}
          onHover={onHover}
          soldOut={isSoldOut}
          bestPrice={bestPrice?.bestPrice}
          bestPriceDuration={bestPrice?.duration}
          bestPriceIsLoading={bestPrice?.loading}
          isSaved={tripPlannerLEItemOfferIds.has(getOfferId(marker.offers[0]))}
          map={map}
        />
      })}
    </>
  )

  return (
    <>
      <MapContainer ref={containerRef}>
        <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline/>}>
          {!isGoogleMapsLoaded && <LoadingIndicator inline/>}
          {isGoogleMapsLoaded && (
            <GoogleMap
              id="le-map"
              zoom={17}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={mapClick}
              onDrag={onInteract}
              onZoomChanged={onInteract}
              onIdle={onIdle}
              mapContainerStyle={MAP_STYLE}
              options={mapOptions}
            >
              {/* The order of elements defines their z-axis stacking. Former elements are below latter elements. */}
              {
                canShowTripPlannerToggle && (
                  <>
                    {
                      (selectedTrip.name !== TRIP_PLANNER_HIDE_ALL_TRIPS_OPTION || selectedTrip.tripId) &&
                      LEandCustomTripPlannerMarkers.map((marker) => {
                        return (
                          <SearchMapMarker
                            key={`tripplanner-marker-${marker.item.id}-${marker.itemPlaceType}`}
                            item={marker.item}
                            position={marker.position}
                            active={selectedTripItem?.id === marker.item.id}
                            onSelect={handleTripPlannerItemSelect}
                            onDeselect={deselectTripItem}
                          />
                        )
                      })
                    }
                    <FadableTripToggle
                      trips={trips}
                      handleSelectTrip={handleSelectTrip}
                      selectedTrip={selectedTrip}
                      dropDownVisible={dropDownVisible && !selectedTripItem}
                      toggleDropdownVisibility={toggleDropdownVisibility}
                      className={cn({ isInBackground: !!selectedTripItem })}
                    />
                    {
                      selectedTripItem && (
                        <TripItemCardSingle
                          cardRef={tripItemCardSingleRef}
                          selectedItem={selectedTripItem}
                          onDeselect={deselectTripItem}
                        />
                      )
                    }
                  </>
                )
              }

              {!showClusterMarkers && nonClusterMarkers}
              {showClusterMarkers &&
                <>
                  {singleMarkers?.filter((marker) => {
                    // Hides lone bedbank marker when clusterMarkets are present
                    return !(areOffersBedbank(marker.offers) && clusterMarkers.length)
                  }).map((marker, index) => {
                    const isSoldOut = !fetchingPage &&
                      marker.offers.every(
                        (offer) => currentOffersBestPrices[getOfferId(offer)]?.soldOut,
                      )
                    const bestPrice = config.businessTraveller.currentAccountMode === 'business' ? currentOffersBestPrices[getOfferId(marker.offers[0])] : undefined
                    return (
                      <HotelMapMarker
                        {...hotelMapMarkerProps}
                        key={marker.id}
                        marker={marker}
                        index={index}
                        isActive={activeMarkerId === marker.id}
                        isVisited={visitedMarkerIds.has(marker.id)}
                        isHovered={hoveredOffer?.id === marker.id}
                        soldOut={isSoldOut}
                        bestPrice={bestPrice?.bestPrice}
                        bestPriceDuration={bestPrice?.duration}
                        bestPriceIsLoading={bestPrice?.loading}
                        isSaved={tripPlannerLEItemOfferIds.has(getOfferId(marker.offers[0]))}
                        handleProductClick={handleProductClick}
                      />
                    )
                  })}
                  {clusterMarkers?.map((cluster) => (
                    <ClusterMarker
                      hoveredOfferId={hoveredOffer?.id}
                      key={`${cluster.latitude}-${cluster.longitude}`}
                      latitude={cluster.latitude}
                      longitude={cluster.longitude}
                      count={cluster.count}
                      onClusterClick={onClusterClick}
                      markers={cluster.markers}
                      filters={filters}
                      onDeselectOffers={onDeselectOffers}
                      handleProductClick={handleProductClick}
                      mapContainerRef={containerRef}
                      onHover={onHover}
                    />
                  ))}
                </>
              }

            </GoogleMap>
          )}

          {!displayMarkers({ currentOfferCount: currentOffers.length, markersToDisplay: offerListOverride?.offerIds.length || 0, fetchingList, isNewManualSearch, isNewlyLoaded, searchStartTimeMillis }) && (
            <SearchTimeLogger event="map-loading-time" data={filters}>
              <FloatingBanner>
                <TextButton
                  kind="secondary"
                  variant="dark"
                  nonInteractable={!allOffersLoaded}
                  endIcon={<ColourfulLoadingAnimation size={8}/>}
                >
                  Loading offers
                </TextButton>
              </FloatingBanner>
            </SearchTimeLogger>
          )}
        </EnsureGoogleMapsLoaded>
      </MapContainer>
      {zeroResultsBanner && fullScreenMap && !fetchingList && currentSearchResult.noOffersAvailable && (
        <FloatingBanner>
          <Snackbar
            description="No offers found in this area"
            kind="neutral"
          />
        </FloatingBanner>
      )}
    </>
  )
}

export default React.memo(SearchResultsMap)
